import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ClassActivity } from '@/types/activity';
import { parseDateParts } from './activityTheme';

interface UpcomingActivitiesWidgetProps {
  upcomingActivities: ClassActivity[];
  onSelectActivity: (activity: ClassActivity) => void;
  onViewAll: () => void;
}

export function UpcomingActivitiesWidget({
  upcomingActivities,
  onSelectActivity,
  onViewAll,
}: UpcomingActivitiesWidgetProps) {
  const dateToneColors = [
    'bg-blue-500/15 text-blue-400 border-blue-500/30',
    'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    'bg-amber-500/15 text-amber-400 border-amber-500/30',
    'bg-purple-500/15 text-purple-400 border-purple-500/30',
  ];

  return (
    <div className="card bg-surface border border-line rounded-[16px] p-5 shadow-2xs flex flex-col gap-4">
      <h3 className="text-base font-bold text-ink tracking-tight">Upcoming Activities</h3>

      {upcomingActivities.length === 0 ? (
        <p className="text-xs text-ink-3 font-medium py-3 text-center">
          No upcoming activities scheduled.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {upcomingActivities.map((item, idx) => {
            const { day, month } = parseDateParts(item.dueDate);

            return (
              <div
                key={item.uuid}
                onClick={() => onSelectActivity(item)}
                className="flex items-center gap-3 p-2 rounded-[10px] hover:bg-surface-2 transition-colors cursor-pointer group"
              >
                {/* Date Badge */}
                <div
                  className={cn(
                    'flex flex-col items-center justify-center h-11 w-11 shrink-0 rounded-[8px] border text-center font-bold font-mono',
                    dateToneColors[idx % dateToneColors.length],
                  )}
                >
                  <span className="text-xs leading-none">{day}</span>
                  <span className="text-[9px] uppercase tracking-wider leading-none mt-0.5">
                    {month}
                  </span>
                </div>

                {/* Title & Info */}
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-bold text-ink group-hover:text-indigo-400 transition-colors line-clamp-1">
                    {item.title}
                  </h4>
                  <p className="text-xs text-ink-3 font-medium mt-0.5">
                    Due {formatDate(item.dueDate)} · {item.classSection.displayName}
                  </p>
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={onViewAll}
            className="text-xs font-bold text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center gap-1.5 pt-1 text-left cursor-pointer"
          >
            <span>View all upcoming</span>
            <span>→</span>
          </button>
        </div>
      )}
    </div>
  );
}
